import { memo, ReactNode } from 'react';

interface FilterPillProps {
  /** Display content (text, icons, badges, etc.) */
  children: ReactNode;
  /** Count to display on the right */
  count?: number;
  /** Whether this filter is active */
  isActive: boolean;
  /** Whether this filter is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
  /** Optional test ID */
  testId?: string;
}

/**
 * FilterPill - Reusable filter button component
 *
 * Used across posts, gear, and other filter interfaces.
 * Black/blue/grey color scheme with consistent styling.
 */
export const FilterPill = memo(function FilterPill({
  children,
  count,
  isActive,
  disabled = false,
  onClick,
  testId,
}: FilterPillProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`
        flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer
        ${
          isActive
            ? 'bg-slate-800 text-white shadow-sm'
            : disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-slate-200 hover:text-slate-800'
        }
      `}
    >
      {children}
      {count !== undefined && (
        <span
          className={`text-xs ${isActive ? 'text-white' : disabled ? 'text-gray-400' : 'text-gray-600'}`}
        >
          {count}
        </span>
      )}
    </button>
  );
});
